import type { ReactNode } from 'react'
import { useMemo, useCallback } from 'react'

import type { AllListingsFilters } from '@b-stock/search-api-client'

import {
  useListingSearch,
  type ListingFiltersState,
} from '@/components/listings/search/ListingSearchProvider'
import type { KeysMatching } from '@/helpers/types/KeysMatching'

import type { AppliedProps } from '../types'
import AppliedFilterSection from './AppliedFilterSection'
import type { RecordType as CategoricalRecordType } from './CategoricalFilter'
import type { Range } from './commonTypes'
import {
  IntegerFormat,
  CurrencyFormat,
} from './NumericRangeFilter/RangeFormats'
import { formatNamesToKeys, formatKeysToNames } from './regionFilterUtils'

const filterStringValue = <T extends { value?: unknown }>(
  option: T
): option is T & { value: string } => typeof option.value === 'string'

const useFilterOptions = (
  availableFilterSet?: AllListingsFilters[keyof AllListingsFilters],
  toDisplayName?: (option: string) => ReactNode,
  // Some raw facet values are synonyms that should render as a single option
  // (e.g. the legacy 'STANDARD' shipment type and its replacement 'BINDING'
  // both mean "Standard Shipping"). Maps a raw value to the canonical value
  // it should be deduped against; identity if omitted.
  normalizeValue?: (value: string) => string
): CategoricalRecordType[] => {
  return useMemo(() => {
    if (!availableFilterSet) {
      return []
    }

    const seenCanonicalValues = new Set<string>()

    return availableFilterSet
      .filter(filterStringValue)
      .filter((option) => {
        const canonicalValue = normalizeValue
          ? normalizeValue(option.value)
          : option.value
        if (seenCanonicalValues.has(canonicalValue)) {
          return false
        }
        seenCanonicalValues.add(canonicalValue)
        return true
      })
      .map((option) => ({
        key: option.value,
        displayName:
          toDisplayName && option.value
            ? toDisplayName(option.value)
            : option.value,
      }))
  }, [availableFilterSet, toDisplayName, normalizeValue])
}

const createRangeAppliedComponent = (
  filterKey: KeysMatching<ListingFiltersState, Range>,
  label: string,
  FormatComponent: React.ComponentType<{
    lower: number | null
    upper: number | null
  }>
) => {
  const Applied = ({ filtersState, updateFilters, noClose }: AppliedProps) => {
    const range = filtersState[filterKey]

    const value = useMemo(() => {
      if (range.filter(Boolean).length === 0) {
        return null
      }

      const [lower, upper] = range

      return {
        key: filterKey,
        name: <FormatComponent lower={lower || 0} upper={upper} />,
      }
    }, [range])

    const onRemove = () => {
      updateFilters({ [filterKey]: [null, null] })
    }

    if (!value) {
      return <></>
    }

    return (
      <AppliedFilterSection
        label={label}
        values={[value]}
        onRemove={onRemove}
        noClose={noClose}
      />
    )
  }

  return Applied
}

const createNumericRangeAppliedComponent = (
  filterKey: KeysMatching<ListingFiltersState, Range>,
  label: string
) => {
  return createRangeAppliedComponent(filterKey, label, IntegerFormat)
}

const createCurrencyRangeAppliedComponent = (
  filterKey: KeysMatching<ListingFiltersState, Range>,
  label: string,
  currency: string
) => {
  const FormatWithCurrency = ({
    lower,
    upper,
  }: {
    lower: number | null
    upper: number | null
  }) => <CurrencyFormat lower={lower} upper={upper} currency={currency} />
  return createRangeAppliedComponent(filterKey, label, FormatWithCurrency)
}

// TODO: If data changes in the future to only send "New", this can be removed
export const formatConditionValues = (
  values: ListingFiltersState['condition'],
  allConditions: NonNullable<AllListingsFilters['condition']>
): ListingFiltersState['condition'] => {
  // This checks the filters coming in from the search API. If the listings
  // filters data does not send us "Brand New", we should NOT convert "New"
  // back to "Brand New"
  const shouldFormat = allConditions.find(
    (condition) => condition.value === 'Brand New'
  )
  return values.map((value) =>
    value === 'Brand New'
      ? 'New'
      : shouldFormat && value === 'New'
        ? 'Brand New'
        : value
  )
}

const maybeFormatFilters = (
  filters: Partial<ListingFiltersState>,
  allFilters: AllListingsFilters | undefined
) => {
  const regionFilter = filters.region

  if (!regionFilter) {
    return filters
  }

  const formatted = formatNamesToKeys(regionFilter, allFilters?.region)

  return {
    ...filters,
    region: formatted,
  }
}

const createCategoricalAppliedComponent = (
  filterKey: KeysMatching<ListingFiltersState, string[]>,
  label: string,
  toDisplayName: (s: string) => React.ReactNode
) => {
  const Applied = ({ filtersState, updateFilters, noClose }: AppliedProps) => {
    let filter = filtersState[filterKey]
    const searchCtx = useListingSearch()

    if (filterKey === 'region') {
      const allRegions = searchCtx.data?.allFilters.region || []
      filter = formatKeysToNames(filter, allRegions)
    }

    const values = useMemo(() => {
      return filter.map((cat: string) => ({
        key: cat,
        name: toDisplayName(cat),
      }))
    }, [filter])

    const onRemove = useCallback(
      (keyToRemove: string) => {
        const newValue = filter.filter((key: string) => {
          return keyToRemove !== key
        })
        updateFilters({ [filterKey]: newValue })
      },
      [filter, updateFilters]
    )

    return (
      <AppliedFilterSection
        label={label}
        values={values}
        onRemove={onRemove}
        noClose={noClose}
      />
    )
  }

  return Applied
}

const createDistanceRangeAppliedComponent = (
  _filterKey: 'distanceRange',
  label: string,
  toDisplayName: (
    distanceRange: ListingFiltersState['distanceRange']
  ) => React.JSX.Element
) => {
  const Applied = ({ filtersState, updateFilters, noClose }: AppliedProps) => {
    const distanceRange = filtersState.distanceRange

    if (!distanceRange.distance || !distanceRange.zipCode) {
      return <></>
    }

    const values = [
      {
        key: 'distanceRange',
        name: toDisplayName(distanceRange),
      },
    ]

    const onRemove = () => {
      updateFilters({
        distanceRange: {
          distance: null,
          zipCode: null,
          coordinates: null,
        },
      })
    }

    return (
      <AppliedFilterSection
        label={label}
        values={values}
        onRemove={onRemove}
        noClose={noClose}
      />
    )
  }

  return Applied
}

const createSearchQueryAppliedComponent = (_filterKey: 'q', label: string) => {
  const Applied = ({ filtersState, updateFilters, noClose }: AppliedProps) => {
    const searchQuery = filtersState.q

    if (!searchQuery) {
      return <></>
    }

    const values = [
      {
        key: 'q',
        name: searchQuery,
      },
    ]

    const onRemove = () => {
      updateFilters({
        q: null,
      })
    }

    return (
      <AppliedFilterSection
        label={label}
        values={values}
        onRemove={onRemove}
        noClose={noClose}
      />
    )
  }

  return Applied
}

const createPercentageValueAppliedComponent = (
  filterKey: KeysMatching<ListingFiltersState, number | null>,
  label: string
) => {
  const Applied = ({ filtersState, updateFilters, noClose }: AppliedProps) => {
    const percentage = filtersState[filterKey]

    const value = useMemo(() => {
      if (percentage === null) {
        return null
      }

      return {
        key: filterKey,
        name: `${percentage}%`,
      }
    }, [percentage])

    const onRemove = useCallback(() => {
      updateFilters({ [filterKey]: null })
    }, [updateFilters])

    if (!value) {
      return <></>
    }

    return (
      <AppliedFilterSection
        label={label}
        values={[value]}
        onRemove={onRemove}
        noClose={noClose}
      />
    )
  }

  return Applied
}

export {
  useFilterOptions,
  createNumericRangeAppliedComponent,
  createCurrencyRangeAppliedComponent,
  createCategoricalAppliedComponent,
  createDistanceRangeAppliedComponent,
  createSearchQueryAppliedComponent,
  createPercentageValueAppliedComponent,
  maybeFormatFilters,
}
